import { Browser } from "@capacitor/browser";
import cx from "classnames";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  type LoaderFunctionArgs,
  useLoaderData,
  useNavigate,
} from "react-router-dom";

import { useConfetti } from "@/hooks";
import { RewardProgressBar } from "@/modules/onboarding/components/RewardProgressBar/RewardProgressBar";
import { get } from "api";
import { RewardStatus, ShopDisplayType } from "api/types";
import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import OpenRewardXL from "atoms/icon/icons/ic_open_reward_XL_ol.svg";
import RewardXL from "atoms/icon/icons/ic_reward_XL_ol.svg";
import { TextButton } from "atoms/text-button/TextButton";
import { AnimatedBackground } from "components/AnimatedBackground";
import { AvailableRewardList } from "organisms/available-reward-list/AvailableRewardList";
import { queryClient } from "pages/Root";
import { getBookAppointmentAction } from "services/HelperService";
import { waitObj } from "toolbox/Promise";
import { defineLoader } from "toolbox/Router";
import pluralize from "utils/pluralize";
import { queueTaskInBackground } from "utils/queueTaskInBackground";

async function getLastOrderLocation() {
  try {
    const ordersData = await queryClient.fetchQuery(["/user/orders"], () =>
      get("/user/orders", { page: 1 }).then((response) => ({
        pages: [response],
        pageParams: [1],
      }))
    );

    const lastOrderLocationId = ordersData.pages[0].items?.[0]?.locationId;

    return (
      (lastOrderLocationId &&
        (await queryClient.fetchQuery(
          [
            "/platform-location/{locationId}",
            { locationId: lastOrderLocationId },
          ],
          () =>
            get("/platform-location/{locationId}", {
              locationId: lastOrderLocationId,
            })
        ))) ||
      null
    );
  } catch (error) {
    console.error("Failed to fetch last order location:", error);
    return null;
  }
}

const load = async ({ request: { url } }: LoaderFunctionArgs) => {
  const user = queryClient.fetchQuery(["/user"], () => get("/user"));
  const rewards = queryClient.fetchQuery(["/rewards"], () =>
    get("/rewards", {})
  );
  const platform = queryClient.fetchQuery(["/platform"], () =>
    get("/platform")
  );

  const searchParams = new URL(url).searchParams;

  const rewardProgress = searchParams.get("rewardProgress");
  const comingFromScan = searchParams.get("comingFromScan");
  const shopDisplayOrderId = searchParams.get("shopDisplayOrderId");
  const orderTransactionId = searchParams.get("orderTransactionId");
  const isComingFromScan = comingFromScan === "true";

  if (!rewardProgress || !Number.isFinite(Number.parseInt(rewardProgress))) {
    throw new Error("Invalid rewardProgress");
  }

  const lastOrderLocation = !isComingFromScan
    ? await getLastOrderLocation()
    : null;

  // Fetch the order if we have an orderId (coming from checkout)
  const shopDisplayOrder = shopDisplayOrderId
    ? await queryClient
        .fetchQuery(
          ["/orders/{shopDisplayOrderId}", { shopDisplayOrderId }],
          () =>
            get("/orders/{shopDisplayOrderId}", {
              shopDisplayOrderId: shopDisplayOrderId,
            })
        )
        .catch((error) => {
          console.error("Failed to fetch order:", error);
          return null;
        })
    : null;

  return waitObj({
    user,
    rewards,
    platform,
    lastOrderLocation,
    rewardProgress: Number.parseInt(rewardProgress),
    comingFromScan: isComingFromScan,
    shopDisplayOrderId,
    orderTransactionId,
    shopDisplayOrder,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const checkoutRewardTrackerLoader = defineLoader((args) => {
  return load(args);
});

export const CheckoutRewardTrackerPage = () => {
  const { triggerConfetti } = useConfetti();
  const isV3Enabled = true;

  const navigate = useNavigate();
  const {
    rewards,
    platform,
    user,
    lastOrderLocation,
    shopDisplayOrderId,
    orderTransactionId,
    shopDisplayOrder,
    ...data
  } = useLoaderData() as LoaderData;
  const [rewardProgress, setRewardProgress] = useState(data.rewardProgress);

  // Calculate what's in the order from shopDisplayOrder (ignore query params)
  // Memoized to prevent unnecessary recalculations on every render
  const calculatedHasTreatmentsOrPackages = useMemo(
    () =>
      shopDisplayOrder?.shopDisplayOrderLineItems?.some(
        (item) =>
          item.displayType === ShopDisplayType.Package ||
          item.displayType === ShopDisplayType.Treatment
      )
        ? "1"
        : "0",
    [shopDisplayOrder?.shopDisplayOrderLineItems]
  );

  const calculatedHasMembershipRewards = useMemo(
    () =>
      shopDisplayOrder?.shopDisplayOrderLineItems?.some(
        (item) => item.shopDisplayMembershipId != null
      )
        ? "1"
        : "0",
    [shopDisplayOrder?.shopDisplayOrderLineItems]
  );

  const calculatedHasRetailProducts = useMemo(
    () =>
      shopDisplayOrder?.shopDisplayOrderLineItems?.some(
        (item) =>
          item.displayType === ShopDisplayType.Product ||
          item.displayType === ShopDisplayType.ProductFromExternalSource
      )
        ? "1"
        : "0",
    [shopDisplayOrder?.shopDisplayOrderLineItems]
  );

  const calculatedHasGiftCards = useMemo(
    () =>
      shopDisplayOrder?.shopDisplayOrderLineItems?.some(
        (item) => item.displayType === ShopDisplayType.GiftCard
      )
        ? "1"
        : "0",
    [shopDisplayOrder?.shopDisplayOrderLineItems]
  );

  const newlyEarnedAvailableRewards = rewards.filter(
    ({ status, earnedByLatestRewardableEvent }) => {
      if (status !== RewardStatus.Available) {
        return false;
      }

      if (user.hasBeenPreRegistered) {
        return true;
      }

      return earnedByLatestRewardableEvent;
    }
  );

  const futureRewards = rewards.filter(
    ({ status }) => status === RewardStatus.NotAvailable
  );
  const nextReward = futureRewards.length
    ? futureRewards.reduce(
        (acc, curr) =>
          acc.minimumVisits ?? 0 <= (curr.minimumVisits ?? 0) ? acc : curr,
        futureRewards[0]
      )
    : undefined;

  const redeemableRewardCount = newlyEarnedAvailableRewards.length;
  const progressBarValue =
    redeemableRewardCount > 0 ? 100 : nextReward?.unlockProgressPercent || 0;

  const canShopInApp =
    platform.platformSettings?.marketplaceEnabled ||
    platform.platformSettings?.visitCashValue === 0;

  useEffect(() => {
    setTimeout(() => setRewardProgress(progressBarValue));

    queueTaskInBackground(() => {
      setTimeout(() => {
        if (progressBarValue >= 100) {
          triggerConfetti();
        }
      }, 1500);
    });
  }, [progressBarValue]);

  const bookAppointmentHref = getBookAppointmentAction(
    lastOrderLocation?.bookAppointmentUrl,
    lastOrderLocation?.phoneNumber,
    lastOrderLocation?.email
  );

  // Helper function to determine wallet navigation route
  // Memoized to prevent recreation on every render
  const getWalletRoute = useCallback(() => {
    // Priority-based routing: check what's in the order from highest to lowest priority
    if (calculatedHasTreatmentsOrPackages === "1") {
      return "/wallet/treatments";
    }
    if (calculatedHasMembershipRewards === "1") {
      return "/wallet/memberships";
    }
    if (calculatedHasRetailProducts === "1") {
      return "/wallet/products";
    }
    if (calculatedHasGiftCards === "1") {
      return "/wallet/giftcards";
    }
    // Default to treatments
    return "/wallet/treatments";
  }, [
    calculatedHasTreatmentsOrPackages,
    calculatedHasMembershipRewards,
    calculatedHasRetailProducts,
    calculatedHasGiftCards,
  ]);

  const isPartnerEmrBookingEnabled =
    platform.platformSettings?.partnerEmrBookingEnabled;

  // Memoize whether to show the Book button
  const shouldShowBookButton = useMemo(
    () =>
      calculatedHasTreatmentsOrPackages === "1" &&
      !isPartnerEmrBookingEnabled &&
      !!bookAppointmentHref,
    [
      calculatedHasTreatmentsOrPackages,
      isPartnerEmrBookingEnabled,
      bookAppointmentHref,
    ]
  );

  const content = (
    <div className="flex min-h-screen flex-col items-center justify-between gap-7 px-7 pb-3 pt-[calc(56px_+_env(safe-area-inset-top))]">
      <div className="relative flex flex-col gap-7">
        <div className="flex flex-col items-center gap-7">
          <div
            className={cx(
              "flex flex-col items-center gap-7 transition delay-1000",
              rewardProgress == 100 && "opacity-0"
            )}
          >
            <Icon svg={RewardXL} size="xlarge" color="brandColor" />
            <div className="font-serif text-h2">
              {data.comingFromScan ? "Check in successful!" : "Reward tracker"}
            </div>
          </div>
          <div
            className={cx(
              "absolute top-0 flex flex-col items-center gap-7 transition delay-1000",
              rewardProgress != 100 && "opacity-0"
            )}
          >
            <Icon svg={OpenRewardXL} size="xlarge" color="brandColor" />
            <div className="font-serif text-h2">
              {redeemableRewardCount}{" "}
              {pluralize(redeemableRewardCount, "reward", "rewards")} unlocked!
            </div>
          </div>
        </div>
        <div className="px-3 text-center text-body1 text-secondary">
          {data.comingFromScan
            ? `Check in every visit${
                canShopInApp ? ` or shop in-app` : ""
              } for rewards`
            : "Your purchase has helped you earn towards your next reward!"}
        </div>
        <RewardProgressBar progress={rewardProgress ?? progressBarValue} />
        <AvailableRewardList
          rewards={newlyEarnedAvailableRewards.map(
            (
              { title, valueIfBanked, repeatCashEnabled, termsAndConditions },
              index
            ) => ({
              id: index.toString(),
              title,
              valueIfBanked,
              repeatCashEnabled,
              tagDescription: "reward unlocked!",
              disclaimer: termsAndConditions,
            })
          )}
          showTitle={false}
          delay={1000}
        />
      </div>
      <div className="flex w-full flex-col gap-3 py-4">
        {data.comingFromScan ? (
          // Scan flow: Simple buttons
          <>
            <Button fullWidth onClick={() => navigate("/rewards")}>
              View my rewards
            </Button>
            <TextButton onClick={() => navigate("/home")}>
              Continue browsing
            </TextButton>
          </>
        ) : (
          // Checkout flow: View my rewards + Book | My Wallet
          <>
            <Button fullWidth onClick={() => navigate("/rewards")}>
              View my rewards
            </Button>
            <div
              className={
                shouldShowBookButton
                  ? "flex w-full flex-row gap-3"
                  : "flex w-full justify-center"
              }
            >
              {shouldShowBookButton && (
                <Button
                  fullWidth
                  style="secondary"
                  onClick={() => Browser.open({ url: bookAppointmentHref! })}
                >
                  Book
                </Button>
              )}
              <Button
                fullWidth
                style="secondary"
                onClick={() => navigate(getWalletRoute())}
              >
                My wallet
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return isV3Enabled ? (
    <AnimatedBackground mode="light" className="min-h-screen">
      {content}
    </AnimatedBackground>
  ) : (
    <div className="bg-one">{content}</div>
  );
};
